import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ISSUE_FILTERS_STORAGE_PREFIX as STORAGE_PREFIX } from "@/domains/pm/utils/issueFilterStorage";
import {
  EMPTY_ISSUE_FILTER,
  type IssueFilterValue,
} from "@/domains/pm/components/issues/issueFilters";

/**
 * The filter selection and search box of an issue list, remembered per
 * building + project in localStorage so leaving the page and coming back —
 * or reloading it — keeps what the user had narrowed the list to.
 *
 * Keyed by project because a status (and so a status filter) belongs to one
 * project; switching project reads that project's own selection instead of
 * carrying the last one across. The active filters are always visible as
 * chips, so a remembered selection never hides issues silently.
 */
export interface PersistedIssueFilters {
  filter: IssueFilterValue;
  search: string;
}

const EMPTY: PersistedIssueFilters = { filter: EMPTY_ISSUE_FILTER, search: "" };

/** Bump when the stored shape changes incompatibly. */
const STORAGE_VERSION = 1;

/**
 * Null until the viewer, building and project are all known; nothing is read
 * or written meanwhile. Both pages are Projects-view only (BuildingLayout
 * renders them once a project is selected), so a null project is only the
 * transient before the scope settles, never an "All projects" scope to keep.
 *
 * Keyed by user so a shared browser profile never hands one account another's
 * selection, assignees included.
 */
export function issueFiltersStorageKey(
  page: "issues" | "my-issues",
  userId: number | null | undefined,
  buildingId: string | undefined,
  projectId: number | null | undefined
): string | null {
  if (!userId || !buildingId || projectId == null) return null;
  return `${STORAGE_PREFIX}v${STORAGE_VERSION}:${userId}:${page}:${buildingId}:${projectId}`;
}

const isNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every((x) => typeof x === "number" && Number.isFinite(x));
const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === "string");
const optionalBoolean = (v: unknown): boolean | undefined =>
  typeof v === "boolean" ? v : undefined;

/**
 * Parse a stored entry, dropping anything malformed field by field. Storage is
 * written by older builds and by hand in devtools; a bad field must cost that
 * field, never the page.
 */
export function parsePersistedIssueFilters(raw: string | null): PersistedIssueFilters {
  if (!raw) return EMPTY;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return EMPTY;
  }
  if (!data || typeof data !== "object") return EMPTY;
  const { filter, search } = data as { filter?: Record<string, unknown>; search?: unknown };
  const f = filter && typeof filter === "object" ? filter : {};
  return {
    filter: {
      assigneeUserIds: isNumberArray(f.assigneeUserIds) ? f.assigneeUserIds : [],
      disciplineIds: isNumberArray(f.disciplineIds) ? f.disciplineIds : [],
      status: isStringArray(f.status) ? f.status : [],
      hasLinkedDocuments: optionalBoolean(f.hasLinkedDocuments),
      hasComments: optionalBoolean(f.hasComments),
      overdueOnly: optionalBoolean(f.overdueOnly),
    },
    search: typeof search === "string" ? search : "",
  };
}

function read(key: string | null): PersistedIssueFilters {
  if (!key) return EMPTY;
  try {
    return parsePersistedIssueFilters(window.localStorage.getItem(key));
  } catch {
    return EMPTY;
  }
}

function write(key: string | null, value: PersistedIssueFilters) {
  if (!key) return;
  try {
    const isEmpty =
      !value.search &&
      value.filter.assigneeUserIds.length === 0 &&
      value.filter.disciplineIds.length === 0 &&
      value.filter.status.length === 0 &&
      value.filter.hasLinkedDocuments === undefined &&
      value.filter.hasComments === undefined &&
      !value.filter.overdueOnly;
    if (isEmpty) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / quota: the selection still works, it just isn't remembered.
  }
}

/** How long the search box waits for typing to stop before it refetches. */
export const ISSUE_SEARCH_DEBOUNCE_MS = 300;

interface HookState {
  key: string | null;
  value: PersistedIssueFilters;
  /** The search the list is fetched with: `value.search` once typing settles. */
  debouncedSearch: string;
  /**
   * Showing a deep link (`?selected=`) unfiltered. Per field: until the user
   * edits that field it shows empty and storage keeps the remembered value.
   */
  bypassFilter: boolean;
  bypassSearch: boolean;
  /** The deep link last seen, so a new one is noticed and a stale one is not. */
  deepLinkSeen: string | null;
  /**
   * What the user changed since this key was read, kept when a null key
   * settles: the filter dimensions they touched ("all" after Clear filters),
   * and whether they touched the search. Tracked rather than inferred from
   * the values, because clearing a dimension is a choice too.
   */
  touched: readonly string[] | "all";
  editedSearch: boolean;
}

/** The dimensions whose value differs between two filters. */
function changedDimensions(a: IssueFilterValue, b: IssueFilterValue): string[] {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].filter((k) => JSON.stringify(left[k]) !== JSON.stringify(right[k]));
}

/**
 * The dimensions the user touched while the key was unknown, over the stored
 * ones. Statuses are added, never removed: none of the stored ones was on
 * screen while the key was unknown, so — as with a status the catalogue
 * hides — no edit can have meant to remove one. Clear filters still does.
 */
function mergeEdits(
  stored: IssueFilterValue,
  edited: IssueFilterValue,
  touched: readonly string[] | "all"
): IssueFilterValue {
  if (touched === "all") return edited;
  const merged = { ...stored } as unknown as Record<string, unknown>;
  const source = edited as unknown as Record<string, unknown>;
  for (const dimension of touched) {
    if (source[dimension] === undefined) delete merged[dimension];
    else merged[dimension] = source[dimension];
  }
  if (touched.includes("status")) {
    merged.status = [...edited.status, ...stored.status.filter((s) => !edited.status.includes(s))];
  }
  return merged as unknown as IssueFilterValue;
}

function stateFor(key: string | null, bypassFilter: boolean, bypassSearch: boolean): HookState {
  const stored = read(key);
  const value = {
    filter: bypassFilter ? EMPTY.filter : stored.filter,
    search: bypassSearch ? "" : stored.search,
  };
  return {
    key,
    value,
    debouncedSearch: value.search,
    bypassFilter,
    bypassSearch,
    deepLinkSeen: null,
    touched: [],
    editedSearch: false,
  };
}

export interface UsePersistedIssueFiltersOptions {
  /**
   * The status keys the scoped project offers, or `null` while the catalogue
   * is loading. A remembered status not among them (renamed or deleted since,
   * or simply unknown because the catalogue failed and holds only the
   * built-ins) is left out of the QUERY, so the list is never empty for a
   * reason the user cannot see. Storage keeps it through every edit but Clear
   * filters: the user was never shown it, so no edit of the status control
   * can have meant to remove it, and one render cannot tell a deleted status
   * from a failed fetch. Once the catalogue offers it again it reappears and
   * can be removed like any other.
   */
  knownStatuses?: readonly string[] | null;
  /**
   * The `?selected=` issue a deep link names, or null. Opens on an unfiltered
   * list and leaves the remembered selection alone, so the issue is not
   * hidden behind the filters the user last left. Taken whenever a NEW value
   * arrives, not only at mount: an in-app link does not remount the page.
   * Editing the filters or the search ends it for that field only; the other
   * keeps its remembered value in storage. A key change while the link is
   * still in the URL (it lands a render before the project resolves) opens the
   * new key unfiltered too. The page strips the parameter once it has tried
   * to open the issue, so a link that is gone never re-arms anything, and following
   * the same link again arrives as a new value.
   */
  deepLink?: string | null;
}

export function usePersistedIssueFilters(
  storageKey: string | null,
  { knownStatuses = null, deepLink: deepLinkInput = null }: UsePersistedIssueFiltersOptions = {}
) {
  // An empty `?selected=` (a truncated link) names nothing; as a distinct
  // value it would never settle against the null it is stored as.
  const deepLink = deepLinkInput || null;
  const [state, setState] = useState(() => ({
    ...stateFor(storageKey, !!deepLink, !!deepLink),
    deepLinkSeen: deepLink,
  }));

  // Adjusted during render rather than in an effect so the page never fetches
  // once with the wrong filters or search. A new key reads that key's own
  // selection, search included and undebounced. When the key is SETTLING (it
  // was null while the user or project loaded) this is still the same visit:
  // a deep link's bypass carries over, and whatever the user set while
  // waiting wins over storage, dimension by dimension. Any other key change
  // opens the new key unfiltered while a deep link is still in the URL (it is
  // the issue the user is being taken to), and with its own selection
  // otherwise. A new deep link arriving later opens unfiltered like one at mount.
  let current = state;
  if (state.key !== storageKey) {
    const settling = state.key === null;
    const linked = deepLink !== null;
    const next = stateFor(
      storageKey,
      settling ? state.bypassFilter : linked,
      settling ? state.bypassSearch : linked
    );
    // Nothing stored was on screen while the key was null, so an edit that
    // came back to empty (picked, then unpicked) changed nothing worth keeping.
    const edited =
      state.touched === "all"
        ? "all"
        : state.touched.filter((d) =>
            changedDimensions(EMPTY.filter, state.value.filter).includes(d)
          );
    if (settling && (edited === "all" || edited.length > 0)) {
      next.value = {
        ...next.value,
        filter: mergeEdits(next.value.filter, state.value.filter, edited),
      };
      next.touched = edited;
    }
    if (settling && state.editedSearch) {
      next.value = { ...next.value, search: state.value.search };
      next.debouncedSearch = state.debouncedSearch;
      next.editedSearch = true;
    }
    current = { ...next, deepLinkSeen: state.deepLinkSeen };
  }
  if (deepLink !== current.deepLinkSeen) {
    current = deepLink
      ? { ...stateFor(storageKey, true, true), deepLinkSeen: deepLink }
      : { ...current, deepLinkSeen: null };
  }
  if (current !== state) setState(current);

  // Keyed on the statuses' CONTENT: the catalogue array is rebuilt on every
  // refetch (window focus), and a new filter object each time would re-read
  // the page's whole issue scope for nothing.
  const knownKey = knownStatuses ? knownStatuses.join("\u0000") : null;
  const known = useMemo(
    () => (knownKey === null ? null : new Set(knownKey ? knownKey.split("\u0000") : [])),
    [knownKey]
  );
  const selected = current.value.filter;
  const filter = useMemo(() => {
    if (!known) return selected;
    const status = selected.status.filter((s) => known.has(s));
    return status.length === selected.status.length ? selected : { ...selected, status };
  }, [selected, known]);

  // The edit handlers are built on the pruned filter the page shows, so they
  // need the same set to put the hidden statuses back.
  const knownRef = useRef(known);
  useEffect(() => {
    knownRef.current = known;
  }, [known]);

  // Persist what committed, never from inside an updater: React may run an
  // updater twice or throw its render away. The settled search, not every
  // keystroke: storage does not need per-character fidelity, and leaving
  // within the debounce costs at most the last few characters. (No flush on
  // unmount: it would write an entry straight back after sign-out cleared it.)
  const { key, value, debouncedSearch, bypassFilter, bypassSearch } = current;
  useEffect(() => {
    if (bypassFilter && bypassSearch) return;
    const stored = bypassFilter || bypassSearch ? read(key) : null;
    write(key, {
      filter: stored && bypassFilter ? stored.filter : selected,
      search: stored && bypassSearch ? stored.search : debouncedSearch,
    });
  }, [key, selected, debouncedSearch, bypassFilter, bypassSearch]);

  useEffect(() => {
    if (value.search === debouncedSearch) return;
    const handle = setTimeout(
      () =>
        setState((prev) =>
          prev.key === key ? { ...prev, debouncedSearch: prev.value.search } : prev
        ),
      ISSUE_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [key, value.search, debouncedSearch]);

  const setFilter = useCallback(
    (next: IssueFilterValue) =>
      setState((prev) => {
        // The edit is built from the pruned filter the page showed, so put
        // back the statuses it could not show; the user cannot have meant to
        // remove them.
        const known = knownRef.current;
        const hidden = known ? prev.value.filter.status.filter((s) => !known.has(s)) : [];
        const status = hidden.length
          ? [...next.status, ...hidden.filter((s) => !next.status.includes(s))]
          : next.status;
        const filter = status === next.status ? next : { ...next, status };
        const changed = changedDimensions(prev.value.filter, filter);
        return {
          ...prev,
          bypassFilter: false,
          touched: prev.touched === "all" ? "all" : [...new Set([...prev.touched, ...changed])],
          value: { ...prev.value, filter },
        };
      }),
    []
  );
  const setSearch = useCallback(
    (search: string) =>
      setState((prev) => ({
        ...prev,
        bypassSearch: false,
        editedSearch: true,
        value: { ...prev.value, search },
      })),
    []
  );
  // Clearing is immediate, not debounced: the list should empty its search the
  // moment the user asks, as it did before. It is the one edit that does drop
  // hidden statuses and a deep link's remembered selection: it says "nothing".
  const clearAll = useCallback(
    () =>
      setState((prev) => ({
        ...prev,
        bypassFilter: false,
        bypassSearch: false,
        touched: "all",
        editedSearch: true,
        value: EMPTY,
        debouncedSearch: "",
      })),
    []
  );

  return {
    filter,
    search: value.search,
    debouncedSearch,
    setFilter,
    setSearch,
    clearAll,
  };
}
